import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { ISalesWashout } from "@/Washouts/types/Api/Sales";
import { ApproveAllWashoutParams, UpdateWashoutParams, UpdateWashoutStatusParams } from "@/Washouts/types/Api/commons";

// Common fragments
import { CompensationFragment, ReviewFragment } from "../../common/fragments";

export async function approveAllSalesWashout(...params: ApproveAllWashoutParams) {
	const [userId, locationId, commissionCycleId, payPeriodId, position] = params;
	const query = gql`
		mutation approveAllSalesWashout(
			$userId: String!
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$position: String
		) {
			approveAllSalesWashout(
				userId: $userId
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				position: $position
			) {
				success
			}
		}
	`;
	const variables = {
		userId,
		locationId,
		commissionCycleId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"approveAllSalesWashout", { success: boolean }> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.approveAllSalesWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function updateSalesWashout(...params: UpdateWashoutParams) {
	const [locationId, washout] = params;
	const query = gql`
		mutation updateSalesWashout($locationId: String!, $washout: WashoutInput!) {
			updateSalesWashout(locationId: $locationId, input: $washout) {
				_id
				commissionCycleId
				payPeriodId
				userId
				summary {
					...Compensation
				}
			}
		}
		${CompensationFragment}
	`;

	const variables = { locationId, washout };

	try {
		const response: GraphqlQueryResponse<
			"updateSalesWashout",
			Pick<ISalesWashout, "_id" | "commissionCycleId" | "payPeriodId" | "userId" | "summary">
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateSalesWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function updateSalesWashoutStatus(...params: UpdateWashoutStatusParams) {
	const [locationId, commissionCycleId, payPeriodId, userId, input, position] = params;
	const query = gql`
		mutation updateSalesWashoutStatus(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$userId: String!
			$reviewInput: WashoutReviewInput
			$position: String
		) {
			updateSalesWashoutStatus(
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				locationId: $locationId
				reviewInput: $reviewInput
				userId: $userId
				position: $position
			) {
				_id
				commissionCycleId
				payPeriodId
				userId
				position
				status
				review {
					...Review
				}
				employee {
					_id
					position
				}
			}
		}
		${ReviewFragment}
	`;

	const variables = { locationId, commissionCycleId, payPeriodId, userId, position, reviewInput: input };

	try {
		const response: GraphqlQueryResponse<
			"updateSalesWashoutStatus",
			Pick<ISalesWashout, "_id" | "commissionCycleId" | "payPeriodId" | "userId" | "review" | "employee">
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateSalesWashoutStatus;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function generateSalesWashout(locationId: string, commissionCycleId: string, payPeriodId: string, userIds: string[]) {
	const query = gql`
		mutation generateWashouts($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $userIds: [String!]!) {
			generateWashouts(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId, userIds: $userIds) {
				success
			}
		}
	`;
	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		userIds,
	};
	try {
		const response: GraphqlQueryResponse<"generateWashouts", { success: boolean }> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.generateWashouts;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
